import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import type { SxProps, Theme } from '@mui/material';
import { Breakpoint, CircularProgress } from '@mui/material';
import MDBox from '../md/MDBox';
import IconButton from '@mui/material/IconButton';
import {
  XIcon,
  ArrowSquareOutIcon,
  ArrowsOutIcon,
  ArrowsInIcon,
} from '@phosphor-icons/react';
import ApiError from 'src/api/ApiError';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import PrimaryButton from 'src/components/common/PrimaryButton';
import CancelButton from 'src/components/common/CancelButton';
import { NavigateOptions, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import MDButton from '../md/MDButton';
import { useGT } from 'gt-react';

interface ButtonProps {
  text: string;
  disabled?: boolean;
  color?: 'primary' | 'error' | 'info' | 'warning';
  variant?: 'text' | 'outlined' | 'contained';
  onClick?: () => void;
}

interface Props {
  open: boolean;
  title?: React.ReactNode;
  subheader?: React.ReactNode;
  children: React.ReactNode;
  onConfirmButton?: ButtonProps;
  onSecondOptionButton?: ButtonProps;
  additionalButtons?: ButtonProps[];
  onCloseButton: ButtonProps;
  isLoading?: boolean;
  maxWidth?: Breakpoint;
  fullScreen?: boolean;
  fullScreenable?: boolean;
  closeable?: boolean;
  hideActions?: boolean;
  disableBackdropClickClose?: boolean;
  disableEscapeKeyClose?: boolean;
  disableContentOverflow?: boolean;
  error?: ApiError | null;
  openToPath?: NavigateOptions;
  hideCloseButton?: boolean;
  optionsMenu?: React.ReactElement | null;
  dialogSx?: SxProps<Theme>;
}
const Modal = ({
  open,
  title,
  subheader,
  children,
  onConfirmButton,
  onSecondOptionButton,
  additionalButtons,
  onCloseButton,
  isLoading,
  maxWidth,
  fullScreen,
  fullScreenable,
  closeable,
  hideActions,
  disableBackdropClickClose,
  disableEscapeKeyClose,
  disableContentOverflow,
  error,
  openToPath,
  hideCloseButton,
  optionsMenu,
  dialogSx,
}: Props) => {
  const navigate = useNavigate();
  const gt = useGT();
  const [isFullScreen, setIsFullScreen] = useState(fullScreen || false);

  return (
    <Dialog
      id="gently-modal"
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' && disableBackdropClickClose) {
          return;
        }
        if (reason === 'escapeKeyDown' && disableEscapeKeyClose) {
          return;
        }

        // could improve this
        if (onCloseButton.onClick) {
          onCloseButton.onClick();
        }
      }}
      maxWidth={maxWidth || 'sm'}
      fullWidth
      fullScreen={isFullScreen}
      sx={dialogSx}
    >
      {title ? (
        <MDBox display="flex" justifyContent="space-between">
          <DialogTitle id="dialog-title">{title}</DialogTitle>
          <MDBox display="flex" alignItems="center">
            {openToPath && (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => navigate(openToPath)}
                aria-label={gt('Open in current tab', {
                  context: 'modal toolbar, open linked route in this tab',
                })}
                sx={{ m: 2, mr: -2 }}
              >
                <ArrowSquareOutIcon size={20} />
              </IconButton>
            )}
            {optionsMenu}
            {fullScreenable && (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setIsFullScreen(!isFullScreen)}
                aria-label={gt('Toggle fullscreen', {
                  context: 'modal toolbar, expand or exit fullscreen',
                })}
                sx={{ m: 2, mr: -2 }}
              >
                {isFullScreen ? (
                  <ArrowsInIcon size={20} />
                ) : (
                  <ArrowsOutIcon size={20} />
                )}
              </IconButton>
            )}
            {closeable ? (
              <IconButton
                edge="start"
                color="inherit"
                onClick={onCloseButton.onClick}
                aria-label={gt('Close', {
                  context: 'modal toolbar, dismiss dialog',
                })}
                sx={{ m: 2 }}
              >
                <XIcon size={20} />
              </IconButton>
            ) : (
              <MDBox />
            )}
          </MDBox>
        </MDBox>
      ) : null}

      {subheader}

      {isFullScreen ? (
        <MDBox
          sx={{
            width: '100%',
            height: '100%',
            overflowY: disableContentOverflow ? 'hidden' : 'auto',
            // background: '#f0f0f0',
            p: 2,
            border: '1px solid #e0e0e0',
          }}
        >
          {children}
        </MDBox>
      ) : (
        <DialogContent
          sx={
            disableContentOverflow
              ? {
                  overflow: 'hidden',
                  height: '80vh',
                  minHeight: '600px',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 2,
                }
              : undefined
          }
        >
          {children}
        </DialogContent>
      )}

      {!hideActions ? (
        <DialogActions>
          {error ? (
            <MDBox>
              <ErrorMessage error={error} />
            </MDBox>
          ) : null}

          {!hideCloseButton && (
            <CancelButton
              onClick={onCloseButton?.onClick}
              disabled={onCloseButton?.disabled}
            >
              {onCloseButton?.text}
            </CancelButton>
          )}

          {additionalButtons?.map((button, index) => (
            <MDButton
              key={index}
              onClick={button.onClick}
              color={button.color}
              variant={button.variant}
              disabled={button.disabled}
            >
              {button.text}
            </MDButton>
          ))}

          {onSecondOptionButton && (
            <CancelButton
              onClick={onSecondOptionButton.onClick}
              disabled={onSecondOptionButton.disabled}
            >
              {onSecondOptionButton.text}
            </CancelButton>
          )}
          {onConfirmButton && (
            <PrimaryButton
              onClick={onConfirmButton.onClick}
              disabled={onConfirmButton.disabled}
            >
              {onConfirmButton.text}
            </PrimaryButton>
          )}
        </DialogActions>
      ) : null}

      {isLoading && (
        <MDBox
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          <CircularProgress />
        </MDBox>
      )}
    </Dialog>
  );
};

export default Modal;
